/**
 * Type definitions for the authentication module.
 */
import {
	GetAppShowResponse,
	GetCollectionsShowResponse,
	GetScreensShowResponse,
	GetCollectionItemsIndexResponse,
	GetCollectionItemsIndexResponseData,
	GetScreensShowResponseSectionItems,
	GetScreensShowResponseSections,
	GetMediaItemsShowResponse,
	Placement,
	ScreenTab,
	CollectionTab,
	Manifest,
} from 'mytours-read-api-typescript'

/** The OAuth2 access token.
 *
 * Properties are marked as readonly because we store the access token in the Redux state.
 */
export interface AccessToken {
	readonly access_token: string
	readonly token_type: string
	readonly expires_in: number
	readonly refresh_token: string

	/** The timestamp (ms) at which we intend to refresh our access token. */
	readonly refreshAt: number
}

/**
 * Configuration for the OAuth 2 functions.
 */
export interface Config {
	readonly apiBase: string
	readonly clientId: string
	readonly clientSecret: string
}

export const DEVICE_PLATFORM_KEY_ANDROID: DevicePlatform = 'android'
export const DEVICE_PLATFORM_KEY_IOS: DevicePlatform = 'ios'
export const DEVICE_PLATFORM_KEY_MOBILE_WEB: DevicePlatform = 'mobile_web'

export const COLLECTION_SUBTYPE_LIST: string = GetCollectionsShowResponse.SubtypeEnum.CollectionList
export const COLLECTION_SUBTYPE_TOUR: string = GetCollectionsShowResponse.SubtypeEnum.CollectionTour
export const COLLECTION_SUBTYPE_MENU: string = GetCollectionsShowResponse.SubtypeEnum.CollectionMenu

export const COLLECTION_ITEM_TYPE_COLLECTION = GetCollectionItemsIndexResponseData.ItemTypeEnum.Collection
export const COLLECTION_ITEM_TYPE_SCREEN = GetCollectionItemsIndexResponseData.ItemTypeEnum.Screen
export const COLLECTION_ITEM_SUBTYPE_TOUR = GetCollectionItemsIndexResponseData.ItemSubtypeEnum.Tour
export const COLLECTION_ITEM_SUBTYPE_WEB = GetCollectionItemsIndexResponseData.ItemSubtypeEnum.Web
export const COLLECTION_ITEM_SUBTYPE_LIST = GetCollectionItemsIndexResponseData.ItemSubtypeEnum.List
export const COLLECTION_ITEM_SUBTYPE_STORY = GetCollectionItemsIndexResponseData.ItemSubtypeEnum.Story
export const COLLECTION_ITEM_SUBTYPE_MENU = GetCollectionItemsIndexResponseData.ItemSubtypeEnum.Menu
export const COLLECTION_ITEM_SUBTYPE_PANORAMA = GetCollectionItemsIndexResponseData.ItemSubtypeEnum.Panorama

export const SCREEN_SUBTYPE_STORY: string = GetScreensShowResponse.ScreenTypeEnum.Story
export const SCREEN_SUBTYPE_WEB: string = GetScreensShowResponse.ScreenTypeEnum.Web
export const SCREEN_SUBTYPE_PANORAMA: string = GetScreensShowResponse.ScreenTypeEnum.Panorama

export const SCREEN_SECTION_TYPE_AUDIO = GetScreensShowResponseSectionItems.MediaTypeEnum.Audio
export const SCREEN_SECTION_TYPE_IMAGE = GetScreensShowResponseSectionItems.MediaTypeEnum.Image
export const SCREEN_SECTION_TYPE_VIDEO = GetScreensShowResponseSectionItems.MediaTypeEnum.Video
export const SCREEN_SECTION_TYPE_WEB_VIDEO = GetScreensShowResponseSectionItems.MediaTypeEnum.Webvideo
export const SCREEN_SECTION_TYPE_AR = GetScreensShowResponseSectionItems.MediaTypeEnum.Ar
export const SCREEN_SECTION_TYPE_ANIMATION = GetScreensShowResponseSectionItems.MediaTypeEnum.Animation

export const SCREEN_SECTION_REMOTE_TYPE_YOUTUBE = GetScreensShowResponseSectionItems.RemoteTypeEnum.Youtube
export const SCREEN_SECTION_REMOTE_TYPE_VIMEO = GetScreensShowResponseSectionItems.RemoteTypeEnum.Vimeo

export const ITEM_TYPE_COLLECTION = GetScreensShowResponseSectionItems.ItemTypeEnum.Collection
export const ITEM_TYPE_COLLECTION_ITEM = GetScreensShowResponseSectionItems.ItemTypeEnum.CollectionItem
export const ITEM_TYPE_SCREEN = GetScreensShowResponseSectionItems.ItemTypeEnum.Screen

export const ITEM_SUBTYPE_COLLECTION_TOUR = GetScreensShowResponseSectionItems.ItemSubtypeEnum.CollectionTour
export const ITEM_SUBTYPE_COLLECTION_LIST = GetScreensShowResponseSectionItems.ItemSubtypeEnum.CollectionList
export const ITEM_SUBTYPE_COLLECTION_MENU = GetScreensShowResponseSectionItems.ItemSubtypeEnum.CollectionMenu
export const ITEM_SUBTYPE_SCREEN_STORY = GetScreensShowResponseSectionItems.ItemSubtypeEnum.ScreenStory
export const ITEM_SUBTYPE_SCREEN_WEB = GetScreensShowResponseSectionItems.ItemSubtypeEnum.ScreenWeb
export const ITEM_SUBTYPE_SCREEN_PANORAMA = GetScreensShowResponseSectionItems.ItemSubtypeEnum.ScreenPanorama

export const SCREEN_SECTION_TYPE_LINK_GROUP = GetScreensShowResponseSections.SectionTypeEnum.LinkGroup
export const SCREEN_SECTION_TYPE_LOCATION = GetScreensShowResponseSections.SectionTypeEnum.Location
export const SCREEN_SECTION_TYPE_MEDIA_GROUP = GetScreensShowResponseSections.SectionTypeEnum.MediaGroup
export const SCREEN_SECTION_TYPE_TEXT = GetScreensShowResponseSections.SectionTypeEnum.Text
export const SCREEN_SECTION_TYPE_SOCIAL_GROUP = GetScreensShowResponseSections.SectionTypeEnum.SocialGroup
export const SCREEN_SECTION_TYPE_PRICE_GROUP = GetScreensShowResponseSections.SectionTypeEnum.PriceGroup
export const SCREEN_SECTION_TYPE_OPENING_TIME_GROUP = GetScreensShowResponseSections.SectionTypeEnum.OpeningTimeGroup
export const SCREEN_SECTION_TYPE_IMAGE_SLIDER = GetScreensShowResponseSections.SectionTypeEnum.ImageSlider
export const SCREEN_SECTION_TYPE_MENU = GetScreensShowResponseSections.SectionTypeEnum.Menu
export const SCREEN_SECTION_TYPE_QUIZ_QUESTION = GetScreensShowResponseSections.SectionTypeEnum.QuizQuestion
export const SCREEN_SECTION_TYPE_QUIZ_SCORE = GetScreensShowResponseSections.SectionTypeEnum.QuizScore
export const SCREEN_SECTION_TYPE_SINGLE_MEDIA = GetScreensShowResponseSections.SectionTypeEnum.SingleMedia
export const SCREEN_SECTION_TYPE_BADGE_GROUP = GetScreensShowResponseSections.SectionTypeEnum.BadgeGroup
export const SCREEN_SECTION_TYPE_FORM = GetScreensShowResponseSections.SectionTypeEnum.Form
export const SCREEN_SECTION_TYPE_GOOGLE_PLACE = GetScreensShowResponseSections.SectionTypeEnum.GooglePlace

export const SCREEN_SECTION_LAYOUT_ICONS = GetScreensShowResponseSections.LayoutEnum.Icons
export const SCREEN_SECTION_LAYOUT_LIST = GetScreensShowResponseSections.LayoutEnum.List
export const SCREEN_SECTION_LAYOUT_BUTTON = GetScreensShowResponseSections.LayoutEnum.Button
export const SCREEN_SECTION_LAYOUT_LIST_NO_ICON = GetScreensShowResponseSections.LayoutEnum.ListNoIcon
export const SCREEN_SECTION_LAYOUT_LIST_WITH_ICON = GetScreensShowResponseSections.LayoutEnum.ListWithIcon
export const SCREEN_SECTION_LAYOUT_BUTTON_NO_ICON = GetScreensShowResponseSections.LayoutEnum.ButtonNoIcon
export const SCREEN_SECTION_LAYOUT_BUTTON_WITH_ICON = GetScreensShowResponseSections.LayoutEnum.ButtonWithIcon
export const SCREEN_SECTION_LAYOUT_ICON = GetScreensShowResponseSections.LayoutEnum.Icon

export const SCREEN_SECTION_HEADER_LAYOUT_NONE = GetScreensShowResponse.HeaderLayoutEnum.None
export const SCREEN_SECTION_HEADER_LAYOUT_IMAGE_AND_TITLE = GetScreensShowResponse.HeaderLayoutEnum.ImageAndTitle

export const COLLECTION_LIST_LAYOUT_GRID = GetCollectionsShowResponse.ListLayoutEnum.Grid
export const COLLECTION_LIST_LAYOUT_LIST = GetCollectionsShowResponse.ListLayoutEnum.List
export const COLLECTION_LIST_LAYOUT_WIDE = GetCollectionsShowResponse.ListLayoutEnum.Wide

export const INITIAL_LAYOUT_DEFAULT = GetCollectionsShowResponse.InitialLayoutEnum.Default
export const INITIAL_LAYOUT_MAP = GetCollectionsShowResponse.InitialLayoutEnum.Map
export const INITIAL_LAYOUT_SINGLE_PAGE = GetCollectionsShowResponse.InitialLayoutEnum.SinglePage

export const ALLOW_DOWNLOAD_DISABLED = GetCollectionsShowResponse.AllowDownloadEnum.Disabled
export const ALLOW_DOWNLOAD_OPTIONAL = GetCollectionsShowResponse.AllowDownloadEnum.Optional
export const ALLOW_DOWNLOAD_FORCED = GetCollectionsShowResponse.AllowDownloadEnum.Forced
export const ALLOW_DOWNLOAD_FORCED_NATIVE = GetCollectionsShowResponse.AllowDownloadEnum.ForcedNative

export const COLLECTION_SORT_BY_ALPHABETICAL = GetCollectionsShowResponse.SortByEnum.Alphabetical
export const COLLECTION_SORT_BY_DISTANCE = GetCollectionsShowResponse.SortByEnum.Distance
export const COLLECTION_SORT_BY_LIST_ORDER = GetCollectionsShowResponse.SortByEnum.ListOrder

/**
 * App Meta Data - extending so rest of Application does not have to import 'api'
 */
export interface AppMetaData extends GetAppShowResponse {
	/**
	 * Whether the project has any offline content configured. Published in `app.json` but not yet in the
	 * generated client, so it is declared here rather than by hand-widening the generated package.
	 */
	offline_content_available?: boolean
	colors: {
		title_bar_color: string
		text_color: string
		page_background_color: string
		accent_color: string
		download_button_color: string
		start_button_color: string
		delete_button_color: string
		coupon_button_color: string
	}
}

export interface AppMetaDataTab
	extends Omit<CollectionTab, 'initial_layout' | 'subtype'>, Omit<ScreenTab, 'initial_layout' | 'subtype'> {
	tab_type: GetCollectionItemsIndexResponseData.ItemTypeEnum
	subtype: GetCollectionItemsIndexResponseData.ItemSubtypeEnum
}

export interface CollectionItems extends GetCollectionItemsIndexResponse {}

// One row of a collection's items, from either shape the read API returns them in. `screen` is only
// populated for a collection requested with `full`, so it is optional here rather than required as it is
// on the generated `CollectionItemFull` — the items index carries no screen at all, and the store holds
// rows from both requests in the same list.
export interface CollectionItem extends GetCollectionItemsIndexResponseData {
	screen?: GetScreensShowResponse
	media_type?: GetMediaItemsShowResponse.MediaTypeEnum
}

// `logo_placement` is emitted by the v3 serializers but missing from the generated client; old payloads may lack it.
export interface Collection extends GetCollectionsShowResponse {
	logo_placement?: Placement
}

export interface Screen extends GetScreensShowResponse {
	logo_placement?: Placement
}

export interface ScreenSection extends GetScreensShowResponseSections {
	/** Form sections only. */
	form_id?: number
	/** Form sections only — the iframe's accessible name. */
	form_title?: string
	/** Form sections only — the open-framed public form URL. */
	embed_url?: string
}

export interface ScreenSectionItem extends GetScreensShowResponseSectionItems {
	/** 'timecoded' when the video has WebVTT cues available from the live transcription endpoint; 'html' (or undefined) otherwise. Emitted top-level on the (flattened) media-item section item by the v3 read serializer. */
	transcription_format?: 'html' | 'timecoded'
}

export interface MediaItem extends GetMediaItemsShowResponse {}

export interface MapRoute {}

export interface Coordinates {
	latitude: number
	longitude: number
}

export type DevicePlatform = 'ios' | 'android' | 'mobile_web'

export interface ResponseError extends Error {
	status?: number
	statusText?: string
}

export interface AppManifest extends Manifest {
	favorites_enabled: boolean
}
